import { useMutation, useQueryClient } from 'react-query'
import { postJSON } from 'common/services/api'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import {
  CURRENT_STOCK_QUERY_KEY,
  DELIVERY_BATCH_URL,
  STOCK_LOGS_QUERY_KEY
} from './queryConstants'
import { AddStockData, MedicationStockReason } from '../types'
import { useUpdateStockRecord } from './useUpdateStockRecord'

type UseDeliveryProps = {
  residentId: string
  residentMedicationId: string
  onSuccess?: () => void
  onError?: (error: any) => void
}

type DeliveryMedication = {
  residentId: string
  residentMedicationId: string
  quantity: number
  notes: string
}

type DeliveryBatchItem = {
  medications: DeliveryMedication[]
  actionTime: string
  deliveryNotes: string
  witnessIds: string[]
  receivedBy?: { id: string }
  receivedFrom?: string
  media?: string[]
}

export const useDelivery = ({
  residentId,
  residentMedicationId,
  onSuccess,
  onError
}: UseDeliveryProps) => {
  const queryClient = useQueryClient()

  const { updateStockRecord, isLoading: isUpdating } = useUpdateStockRecord({
    onSuccess,
    onError
  })

  const createMutation = useMutation(
    (data: AddStockData) => {
      const actionTime =
        data.actionTime &&
        typeof data.actionTime.isValid === 'function' &&
        data.actionTime.isValid()
          ? dayjs
              .utc(data.actionTime.format('YYYY-MM-DD'))
              .startOf('day')
              .toISOString()
          : dayjs.utc().startOf('day').toISOString()

      const medication: DeliveryMedication = {
        residentId,
        residentMedicationId,
        quantity: data.receivedStock,
        notes: data.description
      }

      const hasMedia = data.media && data.media.length > 0

      const batchItem: DeliveryBatchItem = {
        medications: [medication],
        actionTime: actionTime,
        deliveryNotes: data.description,
        witnessIds: [],
        receivedBy: data.receivedBy ? { id: data.receivedBy } : undefined,
        receivedFrom: data.receivedFrom || undefined,
        media: hasMedia ? [] : undefined
      }

      if (hasMedia && data.media) {
        const formData = new FormData()
        formData.append('batch', JSON.stringify(batchItem))

        data.media.forEach((file, index) => {
          formData.append(`batch.media[${index}]`, file)
        })

        return postJSON({
          url: DELIVERY_BATCH_URL,
          contentType: 'multipart/form-data',
          body: formData
        })
      }

      return postJSON({
        url: DELIVERY_BATCH_URL,
        json: batchItem
      })
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([STOCK_LOGS_QUERY_KEY])
        queryClient.invalidateQueries([CURRENT_STOCK_QUERY_KEY])
        onSuccess?.()
      },
      onError: (error) => {
        console.error('Error saving delivery:', error)
        onError?.(error)
      }
    }
  )

  const updateDelivery = async ({
    recordId,
    data
  }: {
    recordId: string
    data: AddStockData
  }) => {
    let actionTime: string | undefined

    if (
      data.actionTime &&
      typeof data.actionTime.isValid === 'function' &&
      data.actionTime.isValid()
    ) {
      actionTime = dayjs
        .utc(data.actionTime.format('YYYY-MM-DD'))
        .startOf('day')
        .toISOString()
    }

    return updateStockRecord({
      recordId,
      type: MedicationStockReason.RESTOCK,
      data: {
        notes: data.description,
        witnessIds: [],
        receivedBy: data.receivedBy || undefined,
        receivedFrom: data.receivedFrom || undefined,
        actionTime,
        media: data.media
      }
    })
  }

  return {
    createDelivery: createMutation.mutateAsync,
    updateDelivery,
    isLoading: createMutation.isLoading || isUpdating
  }
}

export default useDelivery
